import { IMAGE_NODE, ImageNode } from "@repo/fika-blog/src/nodes/imageNode"
import type { NodeViewRendererProps } from "@repo/fika-blog/src/tiptap/core"
import type { Node } from "@repo/fika-blog/src/tiptap/pm/model"
import type { EditorView } from "@repo/fika-blog/src/tiptap/pm/view"
import { pickBy } from "remeda"

const SIZES = ["small", "medium", "big"] as const
type ImageSize = (typeof SIZES)[number]

type SizeLabels = Record<ImageSize, string>

function sizeIconName(size: ImageSize): string {
  return size === "big" ? "size-full" : `size-${size}`
}

function createSvgIcon(iconName: string): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("width", "18")
  svg.setAttribute("height", "18")
  svg.setAttribute("fill", "none")
  svg.setAttribute("stroke", "currentColor")
  svg.setAttribute("stroke-width", "2")
  svg.setAttribute("stroke-linecap", "round")
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use")
  use.setAttribute("href", `#${iconName}`)
  svg.appendChild(use)
  return svg
}

class ImageEditView {
  node: Node
  view: EditorView
  getPos: NodeViewRendererProps["getPos"]
  dom: HTMLElement
  imgWrapper: HTMLElement
  img: HTMLImageElement
  sizeBar: HTMLElement
  sizeButtons = new Map<string, HTMLButtonElement>()
  altBadge: HTMLButtonElement
  altTextarea: HTMLTextAreaElement
  figcaption: HTMLElement
  titleInput: HTMLTextAreaElement
  selected = false
  placeholder: string
  altPlaceholder: string
  sizeLabels: SizeLabels

  constructor(
    node: Node,
    view: EditorView,
    getPos: NodeViewRendererProps["getPos"],
    placeholder: string,
    altPlaceholder: string,
    sizeLabels: SizeLabels,
  ) {
    this.node = node
    this.view = view
    this.getPos = getPos
    this.placeholder = placeholder
    this.altPlaceholder = altPlaceholder
    this.sizeLabels = sizeLabels

    // Build <figure>
    this.dom = document.createElement("figure")
    this.dom.setAttribute("data-type", IMAGE_NODE)
    this.dom.setAttribute("data-label", "")
    if (node.attrs.size) {
      this.dom.setAttribute("data-size", node.attrs.size)
    }

    // Build img wrapper (needed for size bar + alt badge absolute positioning)
    this.imgWrapper = document.createElement("div")
    this.imgWrapper.className = "image-img-wrapper"

    // Build <img>
    this.img = document.createElement("img")
    this.syncImgAttributes(node)
    this.imgWrapper.appendChild(this.img)

    // Build size bar
    this.sizeBar = document.createElement("div")
    this.sizeBar.className = "image-size-bar image-size-bar-hidden"
    for (const size of SIZES) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "image-size-btn"
      btn.title = this.sizeLabels[size]
      btn.dataset.size = size
      btn.appendChild(createSvgIcon(sizeIconName(size)))
      btn.addEventListener("mousedown", e => {
        e.preventDefault()
        e.stopPropagation()
        this.setSize(size)
      })
      this.sizeBar.appendChild(btn)
      this.sizeButtons.set(size, btn)
    }
    this.imgWrapper.appendChild(this.sizeBar)

    // Build ALT badge — shown when selected, click to edit inline
    this.altBadge = document.createElement("button")
    this.altBadge.type = "button"
    this.altBadge.className = "image-alt-badge image-alt-badge-hidden"
    this.altBadge.textContent = "ALT"
    this.altBadge.addEventListener("mousedown", e => {
      e.preventDefault()
      e.stopPropagation()
      this.openAltEdit()
    })
    this.imgWrapper.appendChild(this.altBadge)

    // Build ALT textarea overlay
    this.altTextarea = document.createElement("textarea")
    this.altTextarea.className = "image-alt-input"
    this.altTextarea.placeholder = this.altPlaceholder ?? ""
    this.altTextarea.value = String(node.attrs.alt ?? "")
    this.altTextarea.rows = 1
    this.altTextarea.addEventListener("blur", this.onAltBlur)
    this.altTextarea.addEventListener("keydown", this.onAltKeyDown)
    this.altTextarea.addEventListener("input", this.onAltInput)
    this.imgWrapper.appendChild(this.altTextarea)

    this.dom.appendChild(this.imgWrapper)

    // Build <figcaption> with <textarea> for inline title editing
    this.figcaption = document.createElement("figcaption")
    this.titleInput = document.createElement("textarea")
    this.titleInput.className = "image-title-input"
    this.titleInput.placeholder = this.placeholder
    this.titleInput.value = node.attrs.title ?? ""
    this.titleInput.rows = 1
    this.figcaption.appendChild(this.titleInput)
    this.updateInputVisibility()
    this.dom.appendChild(this.figcaption)

    this.titleInput.addEventListener("blur", this.onTitleBlur)
    this.titleInput.addEventListener("keydown", this.onTitleKeyDown)

    this.updateSizeBar()
    this.updateAltBadge()
  }

  private setSize(size: ImageSize) {
    const pos = this.getPos()
    if (typeof pos !== "number") return
    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        size,
      }),
    )
  }

  private updateSizeBar() {
    const currentSize = (this.node.attrs.size ?? "medium") as string
    for (const [size, btn] of this.sizeButtons) {
      btn.classList.toggle("image-size-btn-active", size === currentSize)
    }
  }

  private updateAltBadge() {
    const hasAlt = !!this.node.attrs.alt
    this.altBadge.classList.toggle("image-alt-badge-empty", !hasAlt)
    this.altBadge.classList.toggle(
      "image-alt-badge-hidden",
      !this.selected && !hasAlt,
    )
  }

  private autogrowAltTextarea() {
    this.altTextarea.style.height = "auto"
    this.altTextarea.style.height = `${this.altTextarea.scrollHeight}px`
  }

  private openAltEdit() {
    this.altBadge.classList.add("image-alt-badge-editing")
    this.altTextarea.classList.add("image-alt-input-visible")
    this.altTextarea.value = String(this.node.attrs.alt ?? "")
    this.autogrowAltTextarea()
    this.altTextarea.focus()
  }

  private commitAlt() {
    const newAlt = this.altTextarea.value.trim() || null
    if (newAlt === (this.node.attrs.alt ?? null)) return

    const pos = this.getPos()
    if (typeof pos !== "number") return

    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        alt: newAlt,
      }),
    )
  }

  private onAltInput = () => {
    this.autogrowAltTextarea()
  }

  private onAltBlur = () => {
    this.commitAlt()
    this.altBadge.classList.remove("image-alt-badge-editing")
    this.altTextarea.classList.remove("image-alt-input-visible")
  }

  private onAltKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault()
      this.altTextarea.value = String(this.node.attrs.alt ?? "")
      this.altTextarea.blur()
    }
  }

  private syncImgAttributes(node: Node) {
    const attrs = pickBy(
      {
        src: node.attrs.src,
        alt: node.attrs.alt,
        title: node.attrs.title,
        width: node.attrs.width,
        height: node.attrs.height,
      } as Record<string, unknown>,
      val => !!val,
    )
    for (const [key, val] of Object.entries(attrs)) {
      this.img.setAttribute(key, String(val))
    }
    for (const key of ["src", "alt", "title", "width", "height"]) {
      if (!attrs[key]) this.img.removeAttribute(key)
    }
  }

  private updateInputVisibility() {
    const hasTitle = !!this.node.attrs.title
    this.figcaption.classList.toggle(
      "image-title-hidden",
      !hasTitle && !this.selected,
    )
  }

  private commitTitle() {
    const newTitle = this.titleInput.value || null
    if (newTitle === (this.node.attrs.title ?? null)) return

    const pos = this.getPos()
    if (typeof pos !== "number") return

    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        title: newTitle,
      }),
    )
  }

  private onTitleBlur = () => {
    this.commitTitle()
  }

  private onTitleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitTitle()
      this.titleInput.blur()
      this.view.focus()
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.titleInput.value = this.node.attrs.title ?? ""
      this.titleInput.blur()
      this.view.focus()
    }
  }

  update(node: Node) {
    if (node.type !== this.node.type) return false

    this.node = node

    if (node.attrs.size) {
      this.dom.setAttribute("data-size", node.attrs.size)
    } else {
      this.dom.removeAttribute("data-size")
    }

    this.syncImgAttributes(node)

    if (document.activeElement !== this.titleInput) {
      this.titleInput.value = node.attrs.title ?? ""
    }

    if (document.activeElement !== this.altTextarea) {
      this.altTextarea.value = String(node.attrs.alt ?? "")
    }

    this.updateInputVisibility()
    this.updateSizeBar()
    this.updateAltBadge()
    return true
  }

  selectNode() {
    this.selected = true
    this.dom.classList.add("ProseMirror-selectednode")
    this.dom.setAttribute("data-selected", "true")
    this.sizeBar.classList.remove("image-size-bar-hidden")
    this.updateAltBadge()
    this.updateInputVisibility()
  }

  deselectNode() {
    this.selected = false
    this.dom.classList.remove("ProseMirror-selectednode")
    this.dom.removeAttribute("data-selected")
    this.sizeBar.classList.add("image-size-bar-hidden")
    this.updateAltBadge()

    if (document.activeElement === this.altTextarea) {
      this.altTextarea.blur()
    }

    if (document.activeElement === this.titleInput) {
      this.commitTitle()
      this.titleInput.blur()
    }

    this.updateInputVisibility()
  }

  stopEvent(event: Event) {
    const target = event.target as HTMLElement
    if (this.figcaption.contains(target)) return true
    if (this.sizeBar.contains(target)) return true
    if (this.altBadge.contains(target)) return true
    if (this.altTextarea.contains(target)) return true
    return false
  }

  ignoreMutation() {
    return true
  }

  destroy() {
    this.titleInput.removeEventListener("blur", this.onTitleBlur)
    this.titleInput.removeEventListener("keydown", this.onTitleKeyDown)
    this.altTextarea.removeEventListener("blur", this.onAltBlur)
    this.altTextarea.removeEventListener("keydown", this.onAltKeyDown)
    this.altTextarea.removeEventListener("input", this.onAltInput)
  }
}

export function getEditImage(options: {
  placeholder: string
  altPlaceholder: string
  sizeLabels: SizeLabels
}) {
  return ImageNode.extend({
    addNodeView() {
      return (props: NodeViewRendererProps) =>
        new ImageEditView(
          props.node,
          props.editor.view,
          props.getPos,
          options.placeholder,
          options.altPlaceholder,
          options.sizeLabels,
        )
    },
  })
}
